import { AbstractProvider } from 'web3-core';
import { JsonRpcPayload } from 'web3-core-helpers';
import LedgerIcon from '../components/Icons/ProviderIcons/LedgerIcon';
import MetaMaskIcon from '../components/Icons/ProviderIcons/MetaMaskIcon';
import StatusIcon from '../components/Icons/ProviderIcons/StatusIcon';
import imTokenIcon from '../components/Icons/ProviderIcons/imTokenIcon';
import TrustIcon from '../components/Icons/ProviderIcons/TrustIcon';
import CoinbaseIcon from '../components/Icons/ProviderIcons/CoinbaseIcon';
import TokenaryIcon from '../components/Icons/ProviderIcons/TokenaryIcon';
import OperaIcon from '../components/Icons/ProviderIcons/OperaIcon';
import MathWalletIcon from '../components/Icons/ProviderIcons/MathWalletIcon';
import SafePalIcon from '../components/Icons/ProviderIcons/SafePalIcon';
import TokenPocketIcon from '../components/Icons/ProviderIcons/TokenPocketIcon';
import BrowserIcon from '../components/Icons/ProviderIcons/BrowserIcon';
import PortisIcon from '../components/Icons/ProviderIcons/PortisIcon';
import FortmaticIcon from '../components/Icons/ProviderIcons/FormaticIcon';
import WalletConnectIcon from '../components/Icons/ProviderIcons/WalletConnectIcon';
import TrezorIcon from '../components/Icons/ProviderIcons/TrezorIcon';
import BinanceIcon from '../components/Icons/ProviderIcons/BinanceIcon';
import CloverIcon from '../components/Icons/ProviderIcons/CloverIcon';
import { TWalletType, WALLET_TYPES } from '../constants/wallet';
import { cleanUpLedgerTraces, getLedgerProvider } from './ldgerService';
import { connectInPageProvider, getInPageProviderName, removeInPageProviderListeners } from './inPageProviderService';
import { getWalletConnectProvider, removeWalletConnectListeners } from './walletConnectService';
import { treasury } from '../stateManagement/Treasury';
import { getPortisProvider, removePortisEventListeners } from './portisService';
import { getFortmaticProvider } from './fortmaticService';
import { disconnectCoinbase, getCoinbaseProvider } from './coinbaseService';
import { getTrezorProvider, removeTrezorEventListeners } from './trezorService';
import { connectBinanceProvider, removeBinanceProviderListeners } from './binanceService';
import { getCloverProvider, removeCloverProviderListeners } from './cloverService';
import { IConnect } from '../index';
import './toastService';

let provider: AbstractProvider;

const providerNameToValue = (providerName: string) => {
  switch (providerName) {
  case 'Ledger':
    return 'Ledger';

  case 'WalletConnect':
    return 'WalletConnect';

  case 'Fortmatic':
    return 'Fortmatic';

  case 'Portis':
    return 'Portis';

  case 'Rabby':
    return 'Rabby';

  case 'MetaMask':
    return 'MetaMask';

  case 'Status':
    return 'Status';

  case 'imToken':
    return 'imToken';

  case 'Trust':
    return 'Trust';

  case 'Coinbase':
    return 'Coinbase';

  case 'Tokenary':
    return 'Tokenary';

  case 'Opera':
    return 'Opera';

  case 'Math Wallet':
    return 'MathWallet';

  case 'SafePal':
    return 'SafePal';

  case 'TokenPocket':
    return 'TokenPocket';

  case 'Binance':
    return 'Binance';

  case 'Clover':
    return 'Clover';

  default:
    return 'Browser';
  }
};

export const getWalletIcon = (providerName: string) => {
  switch (providerName) {
  case 'Portis':
    return PortisIcon;

  case 'Fortmatic':
    return FortmaticIcon;

  case 'WalletConnect':
    return WalletConnectIcon;

  case 'Ledger':
    return LedgerIcon;

  case 'Trezor':
    return TrezorIcon;

  case 'MetaMask':
    return MetaMaskIcon;

  case 'Status':
    return StatusIcon;

  case 'imToken':
    return imTokenIcon;

  case 'Trust':
    return TrustIcon;

  case 'Coinbase':
    return CoinbaseIcon;

  case 'Tokenary':
    return TokenaryIcon;

  case 'Opera':
    return OperaIcon;

  case 'Math Wallet':
    return MathWalletIcon;

  case 'SafePal':
    return SafePalIcon;

  case 'TokenPocket':
    return TokenPocketIcon;

  case 'Binance':
    return BinanceIcon;

  case 'Clover':
    return CloverIcon;

  default:
    return BrowserIcon;
  }
};

export const getSupportedWallets = () => {
  const providerName = getInPageProviderName();
  const binance = !!window.BinanceChain;

  return [
    {
      label: providerName,
      value: providerNameToValue(providerName),
      Icon: getWalletIcon(providerName),
      connectionType: WALLET_TYPES.inPageProvider,
      mobile: true,
      enabled: !!providerName,
    },
    {
      label: 'Wallet Connect',
      value: providerNameToValue('WalletConnect'),
      Icon: getWalletIcon('WalletConnect'),
      connectionType: WALLET_TYPES.walletConnect,
      mobile: false,
      enabled: true,
    },
    {
      label: 'Fortmatic',
      value: providerNameToValue('Fortmatic'),
      Icon: getWalletIcon('Fortmatic'),
      connectionType: WALLET_TYPES.fortmatic,
      mobile: false,
      enabled: !!treasury.fortmatic,
    },
    {
      label: 'Portis',
      value: providerNameToValue('Portis'),
      Icon: getWalletIcon('Portis'),
      connectionType: WALLET_TYPES.portis,
      mobile: false,
      enabled: !!treasury.portis,
    },
    {
      label: 'Ledger',
      value: providerNameToValue('Ledger'),
      Icon: getWalletIcon('Ledger'),
      connectionType: WALLET_TYPES.ledger,
      mobile: false,
      enabled: navigator?.usb?.getDevices || /firefox/.test(navigator.userAgent.toLowerCase()),
    },
    {
      label: 'Trezor',
      value: providerNameToValue('Trezor'),
      Icon: getWalletIcon('Trezor'),
      connectionType: WALLET_TYPES.trezor,
      mobile: false,
      enabled: navigator?.usb?.getDevices,
    },
    {
      label: 'Coinbase Wallet',
      value: providerNameToValue('Coinbase'),
      Icon: getWalletIcon('Coinbase'),
      connectionType: WALLET_TYPES.coinbase,
      mobile: true,
      enabled: true,
    },
    {
      label: 'Binance',
      value: providerNameToValue('Binance'),
      Icon: getWalletIcon('Binance'),
      connectionType: WALLET_TYPES.binance,
      mobile: true,
      enabled: binance,
    },
    {
      label: 'Clover',
      value: providerNameToValue('Clover'),
      Icon: getWalletIcon('Clover'),
      connectionType: WALLET_TYPES.clover,
      mobile: true,
      enabled: true,
    },
  ];
};

export const getNetworkId = async () => new Promise((resolve, reject) => {
  const request: JsonRpcPayload = {
    jsonrpc: '2.0', method: 'net_version', params: [], id: 67,
  };
  provider.sendAsync(request, (err, res) => {
    if (err) {
      reject(err);
    } else {
      resolve(res.result);
    }
  });
});

export const getBalance = async (address: string) => new Promise((resolve, reject) => {
  const request: JsonRpcPayload = {
    jsonrpc: '2.0', method: 'eth_getBalance', params: [address, 'latest'], id: 1,
  };
  provider.sendAsync(request, (err, res) => {
    if (err) {
      reject(err);
    } else {
      resolve(res.result);
    }
  });
});

export const unsubscribeAllEvents = () => {
  removeWalletConnectListeners();
  removeInPageProviderListeners();
  removePortisEventListeners();
  removeTrezorEventListeners();
  removeBinanceProviderListeners();
  removeCloverProviderListeners();
};

type TConnectWalletProvider = (options: IConnect) => any;

const getWalletProvider: TConnectWalletProvider = (options) => {
  provider = null;
  switch (options.connectionType) {
  case WALLET_TYPES.inPageProvider: {
    return connectInPageProvider(options.rpcUrl, options.networkId);
  }
  case WALLET_TYPES.walletConnect: {
    return getWalletConnectProvider(options.rpcUrl, options.networkId);
  }
  case WALLET_TYPES.portis: {
    return getPortisProvider(options.rpcUrl, options.networkId);
  }
  case WALLET_TYPES.fortmatic: {
    return getFortmaticProvider(options.rpcUrl, options.networkId);
  }
  case WALLET_TYPES.coinbase: {
    return getCoinbaseProvider(options.networkId, options.rpcUrl);
  }
  case WALLET_TYPES.ledger: {
    return getLedgerProvider(options.ledgerPath, options.networkId, options.rpcUrl, options.savePath);
  }
  case WALLET_TYPES.trezor: {
    return getTrezorProvider(options.trezorPath, options.networkId, options.rpcUrl, options.savePath);
  }
  case WALLET_TYPES.binance: {
    return connectBinanceProvider(options.networkId);
  }
  case WALLET_TYPES.clover: {
    return getCloverProvider(options.networkId);
  }
  default: return Promise.reject(new Error('No connection type!'));
  }
};

export const connectWalletProvider: TConnectWalletProvider = async (options) => {
  const result = await getWalletProvider(options);
  provider = result.provider;
  return result;
};

const errorActions = {
  [WALLET_TYPES.ledger]: cleanUpLedgerTraces,
  [WALLET_TYPES.trezor]: () => {},
  [WALLET_TYPES.inPageProvider]: () => {},
  [WALLET_TYPES.walletConnect]: () => {},
  [WALLET_TYPES.portis]: () => {},
  [WALLET_TYPES.fortmatic]: () => {},
  [WALLET_TYPES.coinbase]: () => {},
  [WALLET_TYPES.binance]: () => {},
  [WALLET_TYPES.clover]: () => {},
};

export const onConnectionError = (connectionType: TWalletType) => errorActions[connectionType]?.();

const userDisconnectActions = {
  [WALLET_TYPES.coinbase]: disconnectCoinbase,
  [WALLET_TYPES.inPageProvider]: () => {},
  [WALLET_TYPES.ledger]: () => {},
  [WALLET_TYPES.trezor]: () => {},
  [WALLET_TYPES.walletConnect]: () => {},
  [WALLET_TYPES.portis]: () => {},
  [WALLET_TYPES.fortmatic]: () => {},
  [WALLET_TYPES.binance]: () => {},
  [WALLET_TYPES.clover]: () => {},
};

export const resetProviderConnection = (connectionType: TWalletType) => userDisconnectActions[connectionType]?.();
